import { useEffect, useRef } from "react";
import { subscribeOrdersForDate } from "../services/lunchBuddy";
import { notificationService } from "../services/notificationService";
import { getTodayDateString } from "../lib/date";

interface UseOrderNotificationsProps {
  userEmail: string;
  enabled?: boolean; // Allow disabling the hook
}

export function useOrderNotifications({ userEmail, enabled = true }: UseOrderNotificationsProps) {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const shownOrderIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) {
      // If disabled, cleanup any existing subscription
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      return;
    }

    const today = getTodayDateString();

    // Subscribe to order changes (we will self-filter by date and dedupe)
    const unsubscribe = subscribeOrdersForDate(today, async (payload) => {
      if (payload.action !== "create") return;

      const newOrder = payload.record;
      if (newOrder.date !== today) return; // notify only for today

      // Do not notify for the creator
      if (newOrder.createdBy === userEmail) return;

      // Dedupe same id
      if (shownOrderIdsRef.current.has(newOrder.id)) return;
      shownOrderIdsRef.current.add(newOrder.id);

      if (!notificationService.isEnabledInSettings()) {
        console.log("🔇 Notifications disabled in settings");
        return;
      }

      const permission = notificationService.getPermission();
      if (permission !== "granted") {
        console.log("🔇 Notification permission not granted:", permission);
        return;
      }

      console.log("🔔 Showing order notification for:", newOrder);
      notificationService.showOrderNotification(newOrder.restaurant, newOrder.createdBy, newOrder.deadline);
    });

    unsubscribeRef.current = unsubscribe;

    // Cleanup on unmount or dependency change
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [userEmail, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);
}

export default useOrderNotifications;
